import React from "react";
import {
  ZeroButtonImage,
  ZeroButtonHoverImage,
  ZeroButtonClickedImage,
  PowerButtonImage,
  PowerButtonHoverImage,
  PowerButtonClickedImage,
  SpectrophotometerInfo1,
  SpectrophotometerInfo2,
  SpectrophotometerInfo3,
  SpectrophotometerInfo4,
  SpectrophotometerInfo5,
  SpectrophotometerInfo6,
  SpectrophotometerInfo7,
  SpectrophotometerVector,
  InsideSpectrophotometer,
  TheMathSpectrophotometer,
  PrincipleOfSpectrophotometry,
  MeasuringSampleSpectrophotometry,
  WhatIsLightSpectrophotometry,
  SpectrophotometerSustanceSSpectrum,
} from "../../Icons";
import { isActivationKey } from "@learningblocks/shared-components";

import { Image } from "@learningblocks/shared-components";

export function Vector({ className }: { className: string }) {
  return (
    <Image
      className={className}
      src={SpectrophotometerVector}
      alt="spectrophotometer-vector"
    />
  );
}

enum BTN_STATUS {
  DEFAULT = "",
  CLICK = "CLICK",
  HOVER = "HOVER",
  DISABLED = "DISABLED",
}

const POWER_BUTTON = {
  hover: PowerButtonHoverImage,
  clicked: PowerButtonClickedImage,
  default: PowerButtonImage,
};

interface ButtonProps {
  /**
   * ClassName to set on Component
   */
  className: string;
  /**
   * Callback for when button Clicked
   */
  onClick: () => void;
  /**
   * Boolean to set if button is disabled
   */
  disabled?: boolean;
}

export function PowerButton(props: ButtonProps) {
  const [status, setStatus] = React.useState(BTN_STATUS.DEFAULT);

  const notDisabled = () => !props.disabled;

  const onMouseEnter = () => notDisabled() && setStatus(BTN_STATUS.HOVER);

  const onMouseLeave = () => notDisabled() && setStatus(BTN_STATUS.DEFAULT);

  const onMouseDown = () => notDisabled() && setStatus(BTN_STATUS.CLICK);

  const onMouseUp = () => {
    if (notDisabled()) {
      setStatus(BTN_STATUS.DEFAULT);
      props.onClick();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (notDisabled() && isActivationKey(e)) {
      props.onClick();
    }
  };

  const setButtonImage = () => {
    if (notDisabled()) return getButtonImage(POWER_BUTTON, status);
    else return POWER_BUTTON.clicked;
  };

  return (
    <Image
      className={props.className}
      onMouseEnter={onMouseEnter}
      onMouseDown={onMouseDown}
      onMouseLeave={onMouseLeave}
      onMouseUp={onMouseUp}
      onKeyDown={onKeyDown}
      src={setButtonImage()}
      alt="Power Button"
      tabIndex={0}
    />
  );
}

const ZERO_BUTTON = {
  hover: ZeroButtonHoverImage,
  clicked: ZeroButtonClickedImage,
  default: ZeroButtonImage,
};

export function ZeroButton(props: ButtonProps) {
  const [status, setStatus] = React.useState(BTN_STATUS.DEFAULT);

  const notDisabled = () => !props.disabled;

  const onMouseEnter = () => notDisabled() && setStatus(BTN_STATUS.HOVER);

  const onMouseLeave = () => notDisabled() && setStatus(BTN_STATUS.DEFAULT);

  const onMouseDown = () => notDisabled() && setStatus(BTN_STATUS.CLICK);

  const onMouseUp = () => {
    if (notDisabled()) {
      setStatus(BTN_STATUS.DEFAULT);
      props.onClick();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (notDisabled() && isActivationKey(e)) {
      props.onClick();
    }
  };

  const setButtonImage = () => {
    if (notDisabled()) return getButtonImage(ZERO_BUTTON, status);
    else return POWER_BUTTON.clicked;
  };

  return (
    <Image
      className={props.className}
      onMouseEnter={onMouseEnter}
      onMouseDown={onMouseDown}
      onMouseLeave={onMouseLeave}
      onMouseUp={onMouseUp}
      onKeyDown={onKeyDown}
      src={setButtonImage()}
      alt="Zero Button"
      tabIndex={0}
    />
  );
}

interface SpectrophotometerImageProps {
  className: string;
  lidOpen: boolean;
  absorbanceValue: number;
  relativeAbsorbance: number;
  poweredUp: boolean;
  solution: Array<{ pH: string }>;
  cuvetteId: number;
}

export function SpectrophotometerImage(props: SpectrophotometerImageProps) {
  const renderAbsorbance = () => {
    let absorbance = props.absorbanceValue;
    if (props.absorbanceValue) {
      absorbance = props.absorbanceValue - props.relativeAbsorbance;
    }
    return absorbance.toFixed(3);
  };

  const getSolutionColor = (solutions?: Array<{ pH: string }>) => {
    if (solutions !== null && typeof solutions !== "undefined") {
      let pH = 0;
      for (const sol in solutions) {
        pH = +solutions[sol].pH;
      }

      pH = pH / Object.keys(solutions).length;

      const pHLookup: Record<string | number, string> = {
        "-1": "red",
        2: "rgb(246, 205, 15)",
        6: "rgb(209, 185, 61)",
        6.1: "rgb(212, 184, 41)",
        6.3: "rgb(194, 184, 48)",
        6.5: "rgb(154, 173, 58)",
        6.7: "rgb(119, 145, 59)",
        6.9: "rgb(85, 119, 54)",
        7.1: "rgb(48, 89, 60)",
        7.3: "rgb(1, 61, 70)",
        7.5: "rgb(23, 35, 77)",
        7.7: "rgb(26, 37, 83)",
        7.9: "rgb(32, 39, 91)",
        "8.0": "rgb(3, 31, 92)",
        12: "rgb(43, 45, 123)",
      };

      const match = Object.keys(pHLookup).reduce((a, b) => {
        return Math.abs(parseFloat(b) - parseFloat(pH.toString())) <
          Math.abs(parseFloat(a) - parseFloat(pH.toString()))
          ? b
          : a;
      });

      if (Object.keys(solutions).includes("BTB")) {
        return pHLookup[match];
      } else {
        return "#66cef5";
      }
    }
    return "#66cef5";
  };

  const tagList = [
    <path
      key="0"
      d="M5 229H195V231C195 233.761 192.761 236 190 236H10C7.23858 236 5 233.761 5 231V229Z"
      fill="#999999"
    />,
    <rect key="1" x="13" y="236" width="17" height="4" fill="#333333" />,
    <rect key="2" x="170" y="236" width="17" height="4" fill="#333333" />,
    <path
      key="3"
      d="M14.1669 137.255C15.5537 134.637 18.2738 133 21.2364 133H178.764C181.726 133 184.446 134.637 185.833 137.255L196.112 156.66C197.876 159.99 195.462 164 191.693 164H8.30666C4.53827 164 2.12431 159.99 3.88827 156.66L14.1669 137.255Z"
      fill="#CCCCCC"
    />,
    <path
      key="4"
      d="M0 166C0 158.268 6.26801 152 14 152H186C193.732 152 200 158.268 200 166V218C200 225.732 193.732 232 186 232H14C6.26801 232 0 225.732 0 218V166Z"
      fill="#EEEEEE"
    />,
    <path
      key="5"
      d="M5 161.779C5 160.796 5.79642 160 6.77885 160H77.2212C78.2036 160 79 160.796 79 161.779V224.221C79 225.204 78.2036 226 77.2212 226H6.77884C5.79641 226 5 225.204 5 224.221V161.779Z"
      fill="#CCD8E1"
    />,
    <path
      key="6"
      d="M43.9568 214.437C43.9568 213.946 44.355 213.547 44.8462 213.547H74.5529C75.0442 213.547 75.4424 213.946 75.4424 214.437V222.442C75.4424 222.933 75.0442 223.332 74.5529 223.332H44.8462C44.355 223.332 43.9568 222.933 43.9568 222.442V214.437Z"
      fill="#ED3431"
    />,
    <path
      key="7"
      d="M53.5764 217.255H54.3665C54.5152 217.252 54.6456 217.285 54.7579 217.355C54.8322 217.397 54.89 217.454 54.9313 217.526C54.9709 217.607 54.9907 217.706 54.9907 217.824C54.9907 217.966 54.942 218.095 54.8446 218.211C54.7422 218.331 54.5895 218.392 54.3863 218.395H53.5764V217.255ZM53.0289 220.278H53.5764V218.901H54.4086C54.7802 218.895 55.0626 218.779 55.2558 218.553C55.444 218.338 55.5382 218.1 55.5382 217.839C55.5382 217.645 55.501 217.477 55.4267 217.335C55.3557 217.188 55.2632 217.075 55.1493 216.996C55.0139 216.892 54.8801 216.822 54.748 216.788C54.6159 216.762 54.4788 216.75 54.3368 216.75H53.0289V220.278Z"
      fill="black"
    />,
    <path
      key="8"
      d="M55.7685 219.02C55.7685 219.284 55.7908 219.486 55.8354 219.625C55.8817 219.764 55.9527 219.884 56.0484 219.986C56.1195 220.065 56.2185 220.136 56.3457 220.198C56.4712 220.268 56.6322 220.304 56.8287 220.307C57.0318 220.304 57.1961 220.268 57.3216 220.198C57.3893 220.169 57.4447 220.135 57.4876 220.094C57.5355 220.059 57.576 220.023 57.609 219.986C57.7114 219.884 57.784 219.764 57.827 219.625C57.8682 219.486 57.8889 219.284 57.8889 219.02C57.8889 218.755 57.8682 218.552 57.827 218.41C57.784 218.271 57.7114 218.154 57.609 218.059C57.5413 217.975 57.4455 217.9 57.3216 217.834C57.1961 217.771 57.0318 217.738 56.8287 217.735C56.6322 217.738 56.4712 217.771 56.3457 217.834C56.2185 217.9 56.1195 217.975 56.0484 218.059C55.9527 218.154 55.8817 218.271 55.8354 218.41C55.7908 218.552 55.7685 218.755 55.7685 219.02ZM57.3712 219.025C57.3712 219.196 57.3629 219.322 57.3464 219.402C57.3299 219.484 57.296 219.557 57.2449 219.62C57.2069 219.668 57.1507 219.711 57.0764 219.748C57.0054 219.784 56.9228 219.802 56.8287 219.802C56.7412 219.802 56.6603 219.784 56.586 219.748C56.515 219.711 56.4572 219.668 56.4126 219.62C56.368 219.557 56.3358 219.484 56.316 219.402C56.2961 219.322 56.2862 219.194 56.2862 219.02C56.2862 218.844 56.2961 218.715 56.316 218.633C56.3358 218.553 56.368 218.484 56.4126 218.424C56.4572 218.368 56.515 218.324 56.586 218.291C56.6603 218.257 56.7412 218.24 56.8287 218.24C56.9228 218.24 57.0054 218.257 57.0764 218.291C57.1507 218.324 57.2069 218.368 57.2449 218.424C57.296 218.484 57.3299 218.553 57.3464 218.633C57.3629 218.715 57.3712 218.846 57.3712 219.025Z"
      fill="black"
    />,
    <path
      key="9"
      d="M60.47 220.278H60.9282L61.7481 217.766H61.2007L60.7102 219.526H60.7003L60.1157 217.766H59.7417L59.1571 219.526H59.1472L58.6543 217.766H58.1069L58.9292 220.278H59.385L59.9225 218.519H59.9324L60.47 220.278Z"
      fill="black"
    />,
    <path
      key="10"
      d="M64.0914 219.213V218.797C64.0881 218.466 63.9832 218.207 63.7768 218.018C63.5737 217.832 63.3252 217.738 63.0312 217.735C62.909 217.735 62.7852 217.756 62.6597 217.8C62.5325 217.844 62.4177 217.915 62.3154 218.013C62.2146 218.11 62.1321 218.242 62.0677 218.41C62.0033 218.574 61.9711 218.781 61.9711 219.029C61.9711 219.288 62.0082 219.497 62.0825 219.659C62.1502 219.827 62.2386 219.957 62.3476 220.051C62.4037 220.096 62.464 220.134 62.5284 220.165C62.5895 220.2 62.6522 220.228 62.7166 220.249C62.8454 220.288 62.9776 220.307 63.113 220.307C63.468 220.311 63.7768 220.175 64.0394 219.901L63.6653 219.589C63.5002 219.752 63.3128 219.833 63.1031 219.833C62.9231 219.833 62.7777 219.78 62.6671 219.673C62.5482 219.568 62.4888 219.415 62.4888 219.213H64.0914ZM62.4888 218.797C62.4987 218.602 62.5523 218.454 62.6498 218.354C62.7488 218.259 62.876 218.211 63.0312 218.211C63.1865 218.211 63.3136 218.259 63.4127 218.354C63.5134 218.454 63.5671 218.602 63.5737 218.797H62.4888Z"
      fill="black"
    />,
    <path
      key="11"
      d="M64.6339 220.278H65.1516V218.766C65.1549 218.582 65.2094 218.449 65.3151 218.366C65.4092 218.282 65.5223 218.24 65.6544 218.24C65.7717 218.24 65.8848 218.282 65.9938 218.366L66.3703 217.928C66.2167 217.802 66.0367 217.738 65.8303 217.735C65.5545 217.735 65.3316 217.834 65.1615 218.032H65.1516V217.766H64.6339V220.278Z"
      fill="black"
    />,
    <rect
      key="12"
      x="8.55774"
      y="163.558"
      width="66.8846"
      height="28.9973"
      fill={props.poweredUp ? "#6BC87F" : "#337d44"}
    />,
    <path
      key="13"
      fillRule="evenodd"
      clipRule="evenodd"
      d="M74.5529 163.558H8.55774V164.27V164.448V191.666V192.555V192.555H75.4424L75.4423 192.555L75.4424 191.666L75.4423 163.558H75.2645H74.5529ZM74.5529 164.448H9.44716V191.666H74.5529V164.448Z"
      fill="black"
      fillOpacity="0.3"
    />,
    <path
      key="14"
      d="M47.809 164.448H62.1009L34.0564 191.666H19.7644L47.809 164.448Z"
      fill="white"
      fillOpacity="0.2"
    />,
    <path
      key="15"
      d="M63.8596 164.448H66.0144L38.1067 191.666H35.9519L63.8596 164.448Z"
      fill="white"
      fillOpacity="0.2"
    />,
  ];

  if (props.poweredUp) {
    tagList.push(
      <path
        key="16"
        d="M12.1325 169.073H12.6384L12.9938 167.667L13.3461 169.073H13.8424L14.3803 166.895H13.9064L13.583 168.372L13.2116 166.895H12.7857L12.4143 168.372L12.0909 166.895H11.5946L12.1325 169.073Z"
        fill="#333333"
      />,
      <path
        key="17"
        d="M14.3367 169.073H14.8234L14.9611 168.634H15.6463L15.784 169.073H16.2931L15.5631 166.895H15.0668L14.3367 169.073ZM15.0796 168.234L15.3037 167.44L15.5278 168.234H15.0796Z"
        fill="#333333"
      />,
      <path
        key="18"
        d="M16.8669 169.073H17.3664L18.0581 166.895H17.5746L17.1295 168.445L16.6812 166.895H16.1753L16.8669 169.073Z"
        fill="#333333"
      />,
      <path
        key="19"
        d="M18.304 169.073H19.8281V168.66H18.7842V168.17H19.636V167.76H18.7842V167.308H19.7961V166.895H18.304V169.073Z"
        fill="#333333"
      />,
      <path
        key="20"
        d="M20.1832 169.073H21.5825V168.653H20.6635V166.895H20.1832V169.073Z"
        fill="#333333"
      />,
      <path
        key="21"
        d="M21.8405 169.073H23.3646V168.66H22.3207V168.17H23.1725V167.76H22.3207V167.308H23.3326V166.895H21.8405V169.073Z"
        fill="#333333"
      />,
      <path
        key="22"
        d="M23.7197 169.073H24.1744V167.606L24.9653 169.073H25.4231V166.895H24.9685V168.269L24.2288 166.895H23.7197V169.073Z"
        fill="#333333"
      />,
      <path
        key="23"
        d="M26.6931 169.111C27.2663 169.111 27.5385 168.836 27.5385 168.253V167.84H26.722V168.253H27.0806C27.0806 168.573 26.9717 168.698 26.7028 168.698C26.4178 168.698 26.3057 168.56 26.3057 168.218V167.75C26.3057 167.405 26.4114 167.27 26.6899 167.27C26.9333 167.27 27.0454 167.369 27.0614 167.571H27.5192C27.5 167.1 27.2023 166.857 26.6771 166.857C26.1104 166.857 25.8254 167.142 25.8254 167.725V168.243C25.8254 168.826 26.1072 169.111 26.6931 169.111Z"
        fill="#333333"
      />,
      <path
        key="24"
        d="M28.2903 169.073H28.7706V167.315H29.3533V166.895H27.7076V167.315H28.2903V169.073Z"
        fill="#333333"
      />,
      <path
        key="25"
        d="M29.6139 169.073H30.0942V168.173H30.8434V169.073H31.3237V166.895H30.8434V167.757H30.0942V166.895H29.6139V169.073Z"
        fill="#333333"
      />,
      <path
        key="26"
        d="M31.7835 167.965H32.2798V167.469H31.7835V167.965ZM31.7835 169.073H32.2798V168.576H31.7835V169.073Z"
        fill="#333333"
      />,
      <path
        key="27"
        d="M33.5708 169.073H35.0853V168.666H34.5666V166.895H34.1151C33.9903 167.091 33.8109 167.171 33.5068 167.177V167.545C33.7021 167.542 33.907 167.533 34.0927 167.478V168.666H33.5708V169.073Z"
        fill="#333333"
      />,
      <path
        key="28"
        d="M36.0962 169.111C36.6277 169.111 36.9063 168.855 36.9063 168.359V167.593C36.9063 167.107 36.6309 166.857 36.1122 166.857C35.5839 166.857 35.3245 167.113 35.3245 167.6C35.3245 168.055 35.5518 168.301 35.9649 168.301C36.1602 168.301 36.3107 168.247 36.4324 168.125V168.34C36.4324 168.586 36.3267 168.711 36.1026 168.711C35.9009 168.711 35.808 168.609 35.7952 168.477H35.3405C35.3533 168.881 35.5935 169.111 36.0962 169.111ZM36.0834 167.901C35.8912 167.901 35.7984 167.792 35.7984 167.587C35.7984 167.376 35.8977 167.257 36.109 167.257C36.3235 167.257 36.4324 167.379 36.4324 167.613V167.661C36.3619 167.821 36.2467 167.901 36.0834 167.901Z"
        fill="#333333"
      />,
      <path
        key="29"
        d="M38.0388 169.111C38.5543 169.111 38.8265 168.858 38.8265 168.378V167.59C38.8265 167.107 38.5543 166.857 38.0388 166.857C37.5233 166.857 37.2511 167.107 37.2511 167.59V168.378C37.2511 168.858 37.5233 169.111 38.0388 169.111ZM38.0388 168.711C37.8179 168.711 37.725 168.605 37.725 168.359V167.609C37.725 167.363 37.8179 167.257 38.0388 167.257C38.2597 167.257 38.3526 167.363 38.3526 167.609V168.359C38.3526 168.605 38.2597 168.711 38.0388 168.711Z"
        fill="#333333"
      />,
      <path
        key="30"
        d="M39.1969 169.073H39.6708V167.994C39.7413 167.846 39.8405 167.773 39.9654 167.773C40.0935 167.773 40.1511 167.85 40.1511 168.007V169.073H40.625V167.965C40.625 167.568 40.4457 167.363 40.0999 167.363C39.8949 167.363 39.7413 167.437 39.6132 167.603L39.6004 167.401H39.1969V169.073Z"
        fill="#333333"
      />,
      <path
        key="31"
        d="M41.023 169.073H41.4969V168C41.5706 167.84 41.6634 167.773 41.7755 167.773C41.8907 167.773 41.958 167.84 41.958 167.987V169.073H42.4319V168C42.5087 167.84 42.5984 167.773 42.7136 167.773C42.8289 167.773 42.893 167.84 42.893 167.987V169.073H43.3668V167.936C43.3668 167.561 43.1779 167.363 42.8385 167.363C42.6432 167.363 42.4895 167.43 42.3614 167.6C42.2782 167.443 42.1277 167.363 41.9132 167.363C41.7082 167.363 41.561 167.437 41.4393 167.606L41.4265 167.401H41.023V169.073Z"
        fill="#333333"
      />,
      <path
        key="32"
        d="M11.5881 173.957H12.0748L12.2125 173.519H12.8977L13.0354 173.957H13.5445L12.8145 171.78H12.3182L11.5881 173.957ZM12.331 173.118L12.5551 172.324L12.7793 173.118H12.331Z"
        fill="#333333"
      />,
      <path
        key="33"
        d="M13.7918 173.957H14.7332C15.2423 173.957 15.4728 173.768 15.4728 173.346C15.4728 173.061 15.3672 172.891 15.1046 172.817V172.811C15.3159 172.737 15.4024 172.587 15.4024 172.347C15.4024 171.956 15.1782 171.78 14.6659 171.78H13.7918V173.957ZM14.2721 172.648V172.187H14.6179C14.8516 172.187 14.9285 172.241 14.9285 172.414C14.9285 172.59 14.8452 172.648 14.5923 172.648H14.2721ZM14.2721 173.551V173.054H14.6115C14.8965 173.054 14.9925 173.118 14.9925 173.307C14.9925 173.49 14.9061 173.551 14.6499 173.551H14.2721Z"
        fill="#333333"
      />,
      <path
        key="34"
        d="M16.5151 173.996C17.0594 173.996 17.3252 173.775 17.3252 173.323C17.3252 172.913 17.1491 172.718 16.6399 172.644C16.3326 172.6 16.2205 172.526 16.2205 172.363C16.2205 172.219 16.323 172.135 16.5215 172.135C16.7296 172.135 16.8289 172.228 16.8289 172.417H17.2963C17.2963 171.959 17.0434 171.741 16.5311 171.741C16.0124 171.741 15.7402 171.966 15.7402 172.411C15.7402 172.808 15.9227 172.987 16.4062 173.064C16.7296 173.115 16.8449 173.192 16.8449 173.371C16.8449 173.528 16.7392 173.605 16.5279 173.605C16.2941 173.605 16.1757 173.512 16.1757 173.301H15.7082C15.7082 173.778 15.9611 173.996 16.5151 173.996Z"
        fill="#333333"
      />,
      <path
        key="35"
        d="M18.4822 173.996C19.0842 173.996 19.3627 173.717 19.3627 173.109V172.628C19.3627 172.02 19.0842 171.741 18.4822 171.741C17.8802 171.741 17.6017 172.02 17.6017 172.628V173.109C17.6017 173.717 17.8802 173.996 18.4822 173.996ZM18.4822 173.583C18.194 173.583 18.082 173.445 18.082 173.102V172.635C18.082 172.292 18.194 172.155 18.4822 172.155C18.7704 172.155 18.8824 172.292 18.8824 172.635V173.102C18.8824 173.445 18.7704 173.583 18.4822 173.583Z"
        fill="#333333"
      />,
      <path
        key="36"
        d="M19.7673 173.957H20.2476V173.154H20.5069L20.9392 173.957H21.4867L21.016 173.109C21.2882 173.029 21.4035 172.824 21.4035 172.468C21.4035 171.982 21.1921 171.78 20.6574 171.78H19.7673V173.957ZM20.2476 172.744V172.19H20.6062C20.8399 172.19 20.9232 172.27 20.9232 172.468C20.9232 172.664 20.8399 172.744 20.6062 172.744H20.2476Z"
        fill="#333333"
      />,
      <path
        key="37"
        d="M21.7935 173.957H22.7349C23.244 173.957 23.4745 173.768 23.4745 173.346C23.4745 173.061 23.3688 172.891 23.1063 172.817V172.811C23.3176 172.737 23.4041 172.587 23.4041 172.347C23.4041 171.956 23.1799 171.78 22.6676 171.78H21.7935V173.957ZM22.2738 172.648V172.187H22.6196C22.8533 172.187 22.9302 172.241 22.9302 172.414C22.9302 172.59 22.8469 172.648 22.594 172.648H22.2738ZM22.2738 173.551V173.054H22.6132C22.8982 173.054 22.9942 173.118 22.9942 173.307C22.9942 173.49 22.9078 173.551 22.6516 173.551H22.2738Z"
        fill="#333333"
      />,
      <path
        key="38"
        d="M23.5891 173.957H24.0758L24.2135 173.519H24.8987L25.0364 173.957H25.5455L24.8154 171.78H24.3191L23.5891 173.957ZM24.3319 173.118L24.5561 172.324L24.7802 173.118H24.3319Z"
        fill="#333333"
      />,
      <path
        key="39"
        d="M25.7928 173.957H26.2474V172.491L27.0383 173.957H27.4962V171.78H27.0415V173.154L26.3019 171.78H25.7928V173.957Z"
        fill="#333333"
      />,
      <path
        key="40"
        d="M28.7438 173.996C29.3009 173.996 29.5635 173.736 29.5763 173.176H29.1184C29.1088 173.461 29.0063 173.583 28.7502 173.583C28.4812 173.583 28.3788 173.448 28.3788 173.102V172.635C28.3788 172.289 28.4812 172.155 28.7502 172.155C29.0063 172.155 29.1088 172.276 29.1184 172.564H29.5763C29.5635 172.001 29.3009 171.741 28.7438 171.741C28.1674 171.741 27.8985 172.02 27.8985 172.625V173.112C27.8985 173.717 28.1674 173.996 28.7438 173.996Z"
        fill="#333333"
      />,
      <path
        key="41"
        d="M29.9453 173.957H31.4694V173.544H30.4256V173.054H31.2773V172.644H30.4256V172.193H31.4374V171.78H29.9453V173.957Z"
        fill="#333333"
      />,
      <text
        key="42"
        x="12"
        y="188"
        fill="#333333"
        style={{
          fontSize: "17px",
          fontFamily: "Bio Sans",
          fontWeight: "bold",
        }}
      >
        {renderAbsorbance()}
      </text>
    );
  }

  tagList.push(
    <path
      key="47"
      d="M8.55774 197.003C8.55774 196.512 8.95595 196.113 9.44716 196.113H74.5529C75.0441 196.113 75.4424 196.512 75.4424 197.003V209.633C75.4424 210.125 75.0441 210.523 74.5529 210.523H9.44716C8.95595 210.523 8.55774 210.125 8.55774 209.633V197.003Z"
      fill="#99B2C4"
    />,
    <path
      key="48"
      d="M10.7307 200.059C11.1709 200.059 11.3871 199.878 11.3871 199.512C11.3871 199.176 11.2323 199.018 10.8027 198.96C10.5226 198.922 10.4185 198.85 10.4185 198.693C10.4185 198.541 10.5279 198.458 10.7334 198.458C10.9415 198.458 11.0429 198.546 11.0429 198.728H11.3631C11.3631 198.359 11.1549 198.181 10.7414 198.181C10.3198 198.181 10.0956 198.365 10.0956 198.725C10.0956 199.048 10.2477 199.192 10.6586 199.253C10.9575 199.299 11.0642 199.373 11.0642 199.544C11.0642 199.704 10.9548 199.784 10.736 199.784C10.5039 199.784 10.3865 199.691 10.3865 199.496H10.0663C10.0663 199.878 10.2824 200.059 10.7307 200.059Z"
      fill="white"
    />,
    <path
      key="49"
      d="M12.2603 200.059C12.6179 200.059 12.8233 199.904 12.8393 199.608H12.5325C12.5218 199.728 12.4284 199.8 12.2603 199.8C12.0549 199.8 11.9641 199.696 11.9641 199.472V199.429H12.8473V199.2C12.8473 198.818 12.6392 198.61 12.2443 198.61C11.8547 198.61 11.6439 198.816 11.6439 199.197V199.469C11.6439 199.856 11.8574 200.059 12.2603 200.059ZM11.9641 199.192C11.9641 198.968 12.0522 198.869 12.255 198.869C12.4551 198.869 12.5431 198.968 12.5431 199.192H11.9641Z"
      fill="white"
    />,
    <path
      key="50"
      d="M13.61 200.027H13.8635V199.768H13.7087C13.5726 199.768 13.538 199.726 13.538 199.573V198.89H13.8501V198.642H13.538V198.287H13.2444L13.2231 198.642H12.999V198.89H13.2178V199.584C13.2178 199.896 13.3352 200.027 13.61 200.027Z"
      fill="white"
    />,
    <path
      key="51"
      d="M15.0149 200.027H15.3511L15.634 199.077L15.9168 200.027H16.2477L16.6613 198.642H16.3384L16.0769 199.635L15.7834 198.642H15.4926L15.1991 199.632L14.9376 198.642H14.6014L15.0149 200.027Z"
      fill="white"
    />,
    <path
      key="52"
      d="M17.2195 200.059C17.4143 200.059 17.5583 199.987 17.6624 199.846L17.6731 200.027H17.9452V199.131C17.9452 198.776 17.7665 198.61 17.3849 198.61C17.0194 198.61 16.8326 198.765 16.8112 199.069H17.1208C17.1288 198.938 17.2115 198.869 17.3742 198.869C17.5477 198.869 17.6251 198.952 17.6251 199.128V199.189H17.3315C16.9553 199.189 16.7712 199.347 16.7712 199.632C16.7712 199.904 16.942 200.059 17.2195 200.059ZM17.2995 199.8C17.1608 199.8 17.0887 199.734 17.0887 199.614C17.0887 199.485 17.1661 199.419 17.3742 199.419H17.6251V199.589C17.5557 199.728 17.4463 199.8 17.2995 199.8Z"
      fill="white"
    />,
    <path
      key="53"
      d="M18.5912 200.027H18.9327L19.3997 198.642H19.0742L18.77 199.659L18.4631 198.642H18.1242L18.5912 200.027Z"
      fill="white"
    />,
    <path
      key="54"
      d="M20.1557 200.059C20.5132 200.059 20.7187 199.904 20.7347 199.608H20.4278C20.4172 199.728 20.3238 199.8 20.1557 199.8C19.9502 199.8 19.8595 199.696 19.8595 199.472V199.429H20.7427V199.2C20.7427 198.818 20.5346 198.61 20.1397 198.61C19.7501 198.61 19.5393 198.816 19.5393 199.197V199.469C19.5393 199.856 19.7528 200.059 20.1557 200.059ZM19.8595 199.192C19.8595 198.968 19.9476 198.869 20.1503 198.869C20.3505 198.869 20.4385 198.968 20.4385 199.192H19.8595Z"
      fill="white"
    />,
    <path
      key="55"
      d="M21.0676 200.027H21.3878V198.079H21.0676V200.027Z"
      fill="white"
    />,
    <path
      key="56"
      d="M22.3341 200.059C22.6916 200.059 22.8971 199.904 22.9131 199.608H22.6062C22.5956 199.728 22.5022 199.8 22.3341 199.8C22.1286 199.8 22.0379 199.696 22.0379 199.472V199.429H22.9211V199.2C22.9211 198.818 22.713 198.61 22.3181 198.61C21.9285 198.61 21.7177 198.816 21.7177 199.197V199.469C21.7177 199.856 21.9312 200.059 22.3341 200.059ZM22.0379 199.192C22.0379 198.968 22.1259 198.869 22.3287 198.869C22.5289 198.869 22.6169 198.968 22.6169 199.192H22.0379Z"
      fill="white"
    />,
    <path
      key="57"
      d="M23.246 200.027H23.5662V199.128C23.6436 198.97 23.745 198.893 23.8757 198.893C24.0092 198.893 24.0732 198.97 24.0732 199.141V200.027H24.3934V199.112C24.3934 198.781 24.2466 198.61 23.9585 198.61C23.777 198.61 23.6409 198.677 23.5289 198.832L23.5182 198.642H23.246V200.027Z"
      fill="white"
    />,
    <path
      key="58"
      d="M25.3121 200.545C25.723 200.545 25.9258 200.369 25.9258 199.992V198.642H25.6536L25.643 198.832C25.5336 198.68 25.3975 198.61 25.2107 198.61C24.8905 198.61 24.7091 198.805 24.7091 199.181V199.403C24.7091 199.779 24.8905 199.976 25.2107 199.976C25.3761 199.976 25.5016 199.923 25.6056 199.803V199.992C25.6056 200.201 25.5149 200.286 25.3068 200.286C25.144 200.286 25.0479 200.233 25.0346 200.102H24.7358C24.7518 200.406 24.9519 200.545 25.3121 200.545ZM25.2721 199.71C25.1173 199.71 25.0293 199.611 25.0293 199.4V199.187C25.0293 198.978 25.1173 198.877 25.2748 198.877C25.4215 198.877 25.5336 198.968 25.6056 199.133V199.451C25.5336 199.619 25.4215 199.71 25.2721 199.71Z"
      fill="white"
    />,
    <path
      key="59"
      d="M26.7585 200.027H27.012V199.768H26.8572C26.7212 199.768 26.6865 199.726 26.6865 199.573V198.89H26.9987V198.642H26.6865V198.287H26.393L26.3716 198.642H26.1475V198.89H26.3663V199.584C26.3663 199.896 26.4837 200.027 26.7585 200.027Z"
      fill="white"
    />,
    <path
      key="60"
      d="M27.2536 200.027H27.5738V199.128C27.6512 198.97 27.7526 198.893 27.886 198.893C28.0168 198.893 28.0808 198.97 28.0808 199.141V200.027H28.401V199.112C28.401 198.781 28.2542 198.61 27.9661 198.61C27.806 198.61 27.6779 198.664 27.5738 198.784V198.079H27.2536V200.027Z"
      fill="white"
    />,
    <path
      key="61"
      d="M30.1121 200.374V200.129C29.8453 200 29.7119 199.696 29.7119 199.211C29.7119 198.725 29.8453 198.421 30.1121 198.293V198.047C29.6639 198.165 29.4104 198.586 29.4104 199.211C29.4104 199.835 29.6639 200.257 30.1121 200.374Z"
      fill="white"
    />,
    <path
      key="62"
      d="M30.3545 200.027H30.6747V199.128C30.752 198.97 30.8534 198.893 30.9842 198.893C31.1176 198.893 31.1816 198.97 31.1816 199.141V200.027H31.5018V199.112C31.5018 198.781 31.3551 198.61 31.0669 198.61C30.8855 198.61 30.7494 198.677 30.6373 198.832L30.6266 198.642H30.3545V200.027Z"
      fill="white"
    />,
    <path
      key="63"
      d="M31.8762 200.027H32.1964V199.136C32.2791 198.965 32.3698 198.893 32.4872 198.893C32.6073 198.893 32.674 198.962 32.674 199.115V200.027H32.9942V199.133C33.0769 198.965 33.1703 198.893 33.2904 198.893C33.4078 198.893 33.4745 198.962 33.4745 199.115V200.027H33.7947V199.082C33.7947 198.776 33.6399 198.61 33.3598 198.61C33.189 198.61 33.0583 198.672 32.9462 198.826C32.8822 198.685 32.7541 198.61 32.57 198.61C32.3912 198.61 32.2631 198.68 32.1591 198.832L32.1484 198.642H31.8762V200.027Z"
      fill="white"
    />,
    <path
      key="64"
      d="M34.0225 200.374C34.4708 200.257 34.7243 199.835 34.7243 199.211C34.7243 198.586 34.4708 198.165 34.0225 198.047V198.293C34.2893 198.421 34.4227 198.725 34.4227 199.211C34.4227 199.696 34.2893 200 34.0225 200.129V200.374Z"
      fill="white"
    />,
    <rect
      key="65"
      x="9.98083"
      y="202.162"
      width="64.0385"
      height="6.93801"
      rx="3.469"
      fill="#EEEEEE"
    />,
    <mask
      key="66"
      id="mask0"
      mask-type="alpha"
      maskUnits="userSpaceOnUse"
      x="9"
      y="202"
      width="66"
      height="8"
    >
      ,
      <rect
        key="67"
        x="9.98083"
        y="202.162"
        width="64.0385"
        height="6.93801"
        rx="3.469"
        fill="#F7F7F7"
      />
      ,
    </mask>,
    <g key="68" mask="url(#mask0)">
      <path
        opacity="0.1"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.2933 202.162H12.4712V209.1H12.2933V202.162ZM40.7549 202.162H40.9327V209.1H40.7549V202.162ZM26.702 202.162H26.5241V209.1H26.702V202.162ZM54.9857 202.162H55.1636V209.1H54.9857V202.162ZM19.5866 202.162H19.4087V209.1H19.5866V202.162ZM47.8702 202.162H48.0481V209.1H47.8702V202.162ZM33.8174 202.162H33.6395V209.1H33.8174V202.162ZM62.101 202.162H62.2789V209.1H62.101V202.162ZM16.0289 202.162H15.851V209.1H16.0289V202.162ZM44.3125 202.162H44.4904V209.1H44.3125V202.162ZM30.2597 202.162H30.0818V209.1H30.2597V202.162ZM58.5434 202.162H58.7212V209.1H58.5434V202.162ZM23.1443 202.162H22.9664V209.1H23.1443V202.162ZM51.428 202.162H51.6059V209.1H51.428V202.162ZM37.3751 202.162H37.1972V209.1H37.3751V202.162ZM65.6587 202.162H65.8366V209.1H65.6587V202.162ZM72.0626 202.162H71.8847V209.1H72.0626V202.162ZM14.0722 202.162H14.2501V209.1H14.0722V202.162ZM42.7116 202.162H42.5337V209.1H42.7116V202.162ZM28.3029 202.162H28.4808V209.1H28.3029V202.162ZM56.9423 202.162H56.7645V209.1H56.9423V202.162ZM21.1876 202.162H21.3655V209.1H21.1876V202.162ZM49.827 202.162H49.6491V209.1H49.827V202.162ZM35.4183 202.162H35.5962V209.1H35.4183V202.162ZM64.0578 202.162H63.8799V209.1H64.0578V202.162ZM70.1058 202.162H70.2837V209.1H70.1058V202.162ZM17.8078 202.162H17.6299V209.1H17.8078V202.162ZM46.0914 202.162H46.2693V209.1H46.0914V202.162ZM32.0385 202.162H31.8606V209.1H32.0385V202.162ZM60.3221 202.162H60.5V209.1H60.3221V202.162ZM24.9231 202.162H24.7453V209.1H24.9231V202.162ZM53.2068 202.162H53.3847V209.1H53.2068V202.162ZM39.1539 202.162H38.9761V209.1H39.1539V202.162ZM67.4376 202.162H67.6155V209.1H67.4376V202.162ZM73.8414 202.162H73.6635V209.1H73.8414V202.162ZM13.1828 202.162H13.3607V209.1H13.1828V202.162ZM41.8222 202.162H41.6443V209.1H41.8222V202.162ZM27.4135 202.162H27.5914V209.1H27.4135V202.162ZM56.0529 202.162H55.875V209.1H56.0529V202.162ZM20.2981 202.162H20.476V209.1H20.2981V202.162ZM48.9375 202.162H48.7597V209.1H48.9375V202.162ZM34.5289 202.162H34.7068V209.1H34.5289V202.162ZM63.1684 202.162H62.9905V209.1H63.1684V202.162ZM69.2164 202.162H69.3943V209.1H69.2164V202.162ZM16.9183 202.162H16.7405V209.1H16.9183V202.162ZM45.202 202.162H45.3799V209.1H45.202V202.162ZM31.1491 202.162H30.9712V209.1H31.1491V202.162ZM59.4328 202.162H59.6107V209.1H59.4328V202.162ZM24.0337 202.162H23.8558V209.1H24.0337V202.162ZM52.3173 202.162H52.4952V209.1H52.3173V202.162ZM38.2645 202.162H38.0866V209.1H38.2645V202.162ZM66.5482 202.162H66.726V209.1H66.5482V202.162ZM72.952 202.162H72.7741V209.1H72.952V202.162ZM14.9616 202.162H15.1395V209.1H14.9616V202.162ZM43.601 202.162H43.4231V209.1H43.601V202.162ZM29.1924 202.162H29.3703V209.1H29.1924V202.162ZM57.8318 202.162H57.6539V209.1H57.8318V202.162ZM22.077 202.162H22.2549V209.1H22.077V202.162ZM50.7164 202.162H50.5385V209.1H50.7164V202.162ZM36.3077 202.162H36.4856V209.1H36.3077V202.162ZM64.9472 202.162H64.7693V209.1H64.9472V202.162ZM70.9953 202.162H71.1732V209.1H70.9953V202.162ZM18.6971 202.162H18.5193V209.1H18.6971V202.162ZM46.9809 202.162H47.1588V209.1H46.9809V202.162ZM32.9279 202.162H32.7501V209.1H32.9279V202.162ZM61.2116 202.162H61.3895V209.1H61.2116V202.162ZM25.8126 202.162H25.6347V209.1H25.8126V202.162ZM54.0962 202.162H54.2741V209.1H54.0962V202.162ZM40.0433 202.162H39.8654V209.1H40.0433V202.162ZM68.327 202.162H68.5048V209.1H68.327V202.162ZM74.7308 202.162H74.553V209.1H74.7308V202.162Z"
        fill="#777777"
      />
      <rect
        x="12.2933"
        y="205.186"
        width="59.4135"
        height="1.06738"
        rx="0.533692"
        fill="#555555"
      />
    </g>,
    <path
      key="69"
      d="M10.5145 203.407C10.5145 203.014 10.8331 202.696 11.2261 202.696H14.428C14.821 202.696 15.1395 203.014 15.1395 203.407V207.499C15.1395 207.892 14.821 208.21 14.428 208.21H11.2261C10.8331 208.21 10.5145 207.892 10.5145 207.499V203.407Z"
      fill="#FAAC48"
    />,
    <path
      key="70"
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.2261 202.518H14.428C14.9192 202.518 15.3174 202.916 15.3174 203.407V207.499C15.3174 207.99 14.9192 208.388 14.428 208.388H11.2261C10.7349 208.388 10.3367 207.99 10.3367 207.499V203.407C10.3367 202.916 10.7349 202.518 11.2261 202.518ZM11.2261 202.695C10.8331 202.695 10.5146 203.014 10.5146 203.407V207.499C10.5146 207.892 10.8331 208.21 11.2261 208.21H14.428C14.821 208.21 15.1396 207.892 15.1396 207.499V203.407C15.1396 203.014 14.821 202.695 14.428 202.695H11.2261Z"
      fill="#AF7832"
    />,
    <rect
      key="71"
      x="11.5819"
      y="202.696"
      width="0.35577"
      height="5.51482"
      fill="#AF7832"
    />,
    <rect
      key="72"
      x="12.2935"
      y="202.696"
      width="0.355769"
      height="5.51482"
      fill="#AF7832"
    />,
    <rect
      key="73"
      x="13.005"
      y="202.696"
      width="0.355769"
      height="5.51482"
      fill="#AF7832"
    />,
    <rect
      key="74"
      x="13.7164"
      y="202.696"
      width="0.355769"
      height="5.51482"
      fill="#AF7832"
    />,
    <rect
      key="75"
      x="9.26929"
      y="213.547"
      width="30.9519"
      height="9.78437"
      rx="4.89218"
      fill="#003B75"
    />,
    <path
      key="76"
      d="M9.26929 214.437C9.26929 213.946 9.6675 213.547 10.1587 213.547H39.3318C39.823 213.547 40.2212 213.946 40.2212 214.437V222.442C40.2212 222.933 39.823 223.332 39.3318 223.332H10.1587C9.6675 223.332 9.26929 222.933 9.26929 222.442V214.437Z"
      fill="#0063C3"
    />,
    <path
      key="77"
      d="M16.3549 219.774H17.9721V219.411H16.8204L17.9721 217.529V217.241H16.4V217.583H17.5031L16.3549 219.454V219.774Z"
      fill="white"
    />,
    <path
      key="78"
      d="M19.7215 219.009V218.71C19.7191 218.473 19.6456 218.287 19.5008 218.151C19.3584 218.018 19.1841 217.95 18.978 217.948C18.8923 217.948 18.8054 217.963 18.7174 217.995C18.6282 218.026 18.5477 218.077 18.4759 218.148C18.4053 218.217 18.3474 218.312 18.3022 218.432C18.257 218.551 18.2345 218.699 18.2345 218.877C18.2345 219.063 18.2605 219.213 18.3126 219.329C18.3601 219.449 18.4221 219.543 18.4985 219.61C18.5379 219.643 18.5802 219.67 18.6253 219.692C18.6682 219.718 18.7122 219.738 18.7573 219.753C18.8477 219.781 18.9403 219.795 19.0353 219.795C19.2843 219.797 19.5008 219.7 19.685 219.503L19.4227 219.279C19.3069 219.396 19.1754 219.454 19.0283 219.454C18.9021 219.454 18.8002 219.416 18.7226 219.339C18.6392 219.264 18.5975 219.154 18.5975 219.009H19.7215ZM18.5975 218.71C18.6045 218.57 18.6421 218.464 18.7104 218.393C18.7799 218.324 18.8691 218.29 18.978 218.29C19.0868 218.29 19.176 218.324 19.2455 218.393C19.3161 218.464 19.3538 218.57 19.3584 218.71H18.5975Z"
      fill="white"
    />,
    <path
      key="79"
      d="M20.1019 219.774H20.465V218.688C20.4673 218.556 20.5055 218.46 20.5796 218.401C20.6456 218.341 20.725 218.311 20.8176 218.311C20.8998 218.311 20.9792 218.341 21.0556 218.401L21.3196 218.087C21.2119 217.996 21.0857 217.95 20.9409 217.948C20.7475 217.948 20.5912 218.019 20.4719 218.161H20.465V217.97H20.1019V219.774Z"
      fill="white"
    />,
    <path
      key="80"
      d="M21.3335 218.87C21.3335 219.06 21.3492 219.205 21.3804 219.305C21.4129 219.404 21.4627 219.491 21.5298 219.563C21.5796 219.62 21.6491 219.671 21.7383 219.716C21.8263 219.766 21.9392 219.792 22.077 219.795C22.2195 219.792 22.3347 219.766 22.4227 219.716C22.4702 219.696 22.509 219.671 22.5391 219.642C22.5727 219.616 22.6011 219.59 22.6242 219.563C22.696 219.491 22.747 219.404 22.7771 219.305C22.8061 219.205 22.8205 219.06 22.8205 218.87C22.8205 218.68 22.8061 218.534 22.7771 218.432C22.747 218.333 22.696 218.249 22.6242 218.181C22.5768 218.12 22.5096 218.067 22.4227 218.019C22.3347 217.974 22.2195 217.95 22.077 217.948C21.9392 217.95 21.8263 217.974 21.7383 218.019C21.6491 218.067 21.5796 218.12 21.5298 218.181C21.4627 218.249 21.4129 218.333 21.3804 218.432C21.3492 218.534 21.3335 218.68 21.3335 218.87ZM22.4575 218.874C22.4575 218.997 22.4517 219.087 22.4401 219.145C22.4285 219.204 22.4048 219.256 22.3689 219.301C22.3422 219.336 22.3029 219.367 22.2508 219.393C22.201 219.419 22.1431 219.431 22.077 219.431C22.0157 219.431 21.9589 219.419 21.9068 219.393C21.857 219.367 21.8165 219.336 21.7852 219.301C21.7539 219.256 21.7313 219.204 21.7175 219.145C21.7036 219.087 21.6966 218.995 21.6966 218.87C21.6966 218.744 21.7036 218.651 21.7175 218.592C21.7313 218.536 21.7539 218.486 21.7852 218.443C21.8165 218.402 21.857 218.371 21.9068 218.347C21.9589 218.323 22.0157 218.311 22.077 218.311C22.1431 218.311 22.201 218.323 22.2508 218.347C22.3029 218.371 22.3422 218.402 22.3689 218.443C22.4048 218.486 22.4285 218.536 22.4401 218.592C22.4517 218.651 22.4575 218.745 22.4575 218.874Z"
      fill="white"
    />,
    <path
      key="81"
      d="M24.0557 219.774H24.4187V219.584C24.5392 219.72 24.6944 219.79 24.8843 219.795C24.9966 219.792 25.0927 219.77 25.1727 219.727C25.2537 219.685 25.3192 219.629 25.369 219.56C25.4187 219.503 25.4529 219.426 25.4714 219.329C25.4888 219.236 25.4975 219.083 25.4975 218.87C25.4975 218.654 25.4888 218.5 25.4714 218.408C25.4529 218.316 25.4187 218.241 25.369 218.184C25.267 218.032 25.1055 217.954 24.8843 217.948C24.7916 217.948 24.7088 217.967 24.6359 218.005C24.5571 218.046 24.4847 218.098 24.4187 218.161V217.241H24.0557V219.774ZM24.7748 218.311C24.8605 218.311 24.9283 218.325 24.9781 218.354C25.0279 218.386 25.0632 218.428 25.0841 218.483C25.1072 218.537 25.1229 218.596 25.131 218.66C25.1333 218.728 25.1344 218.798 25.1344 218.87C25.1344 218.939 25.1333 219.005 25.131 219.07C25.1229 219.138 25.1072 219.2 25.0841 219.254C25.0632 219.306 25.0279 219.349 24.9781 219.383C24.9283 219.415 24.8605 219.431 24.7748 219.431C24.6938 219.431 24.6295 219.414 24.582 219.379C24.5322 219.346 24.4957 219.302 24.4726 219.247C24.4506 219.193 24.4361 219.132 24.4291 219.065C24.4222 218.999 24.4187 218.934 24.4187 218.87C24.4187 218.801 24.4222 218.734 24.4291 218.671C24.4361 218.607 24.4506 218.548 24.4726 218.493C24.4957 218.439 24.5322 218.395 24.582 218.361C24.6295 218.328 24.6938 218.311 24.7748 218.311Z"
      fill="white"
    />,
    <path
      key="82"
      d="M25.904 219.169C25.9063 219.373 25.9729 219.527 26.1038 219.631C26.2242 219.738 26.3591 219.792 26.5085 219.795C26.7008 219.795 26.8565 219.723 26.9758 219.581H26.9828V219.774H27.3458V217.97H26.9828V219.044C26.9828 219.166 26.9492 219.261 26.882 219.329C26.816 219.397 26.7309 219.431 26.6266 219.431C26.5224 219.431 26.4367 219.397 26.3696 219.329C26.3012 219.261 26.2671 219.166 26.2671 219.044V217.97H25.904V219.169Z"
      fill="white"
    />,
    <path
      key="83"
      d="M27.8357 217.97H27.655V218.247H27.8357V219.329C27.8357 219.471 27.8774 219.58 27.9608 219.656C28.0419 219.734 28.1461 219.774 28.2735 219.774H28.4628V219.431H28.3273C28.2393 219.434 28.1965 219.389 28.1988 219.298V218.247H28.4628V217.97H28.1988V217.421H27.8357V217.97Z"
      fill="white"
    />,
    <path
      key="84"
      d="M28.8259 217.97H28.6452V218.247H28.8259V219.329C28.8259 219.471 28.8676 219.58 28.951 219.656C29.032 219.734 29.1363 219.774 29.2636 219.774H29.453V219.431H29.3175C29.2295 219.434 29.1866 219.389 29.189 219.298V218.247H29.453V217.97H29.189V217.421H28.8259V217.97Z"
      fill="white"
    />,
    <path
      key="85"
      d="M29.7066 218.87C29.7066 219.06 29.7223 219.205 29.7535 219.305C29.786 219.404 29.8358 219.491 29.9029 219.563C29.9527 219.62 30.0222 219.671 30.1114 219.716C30.1994 219.766 30.3123 219.792 30.4501 219.795C30.5926 219.792 30.7078 219.766 30.7958 219.716C30.8433 219.696 30.8821 219.671 30.9122 219.642C30.9458 219.616 30.9742 219.59 30.9973 219.563C31.0691 219.491 31.1201 219.404 31.1502 219.305C31.1792 219.205 31.1936 219.06 31.1936 218.87C31.1936 218.68 31.1792 218.534 31.1502 218.432C31.1201 218.333 31.0691 218.249 30.9973 218.181C30.9498 218.12 30.8827 218.067 30.7958 218.019C30.7078 217.974 30.5926 217.95 30.4501 217.948C30.3123 217.95 30.1994 217.974 30.1114 218.019C30.0222 218.067 29.9527 218.12 29.9029 218.181C29.8358 218.249 29.786 218.333 29.7535 218.432C29.7223 218.534 29.7066 218.68 29.7066 218.87ZM30.8306 218.874C30.8306 218.997 30.8248 219.087 30.8132 219.145C30.8016 219.204 30.7779 219.256 30.742 219.301C30.7153 219.336 30.676 219.367 30.6238 219.393C30.574 219.419 30.5161 219.431 30.4501 219.431C30.3887 219.431 30.332 219.419 30.2799 219.393C30.2301 219.367 30.1896 219.336 30.1583 219.301C30.127 219.256 30.1044 219.204 30.0905 219.145C30.0766 219.087 30.0697 218.995 30.0697 218.87C30.0697 218.744 30.0766 218.651 30.0905 218.592C30.1044 218.536 30.127 218.486 30.1583 218.443C30.1896 218.402 30.2301 218.371 30.2799 218.347C30.332 218.323 30.3887 218.311 30.4501 218.311C30.5161 218.311 30.574 218.323 30.6238 218.347C30.676 218.371 30.7153 218.402 30.742 218.443C30.7779 218.486 30.8016 218.536 30.8132 218.592C30.8248 218.651 30.8306 218.745 30.8306 218.874Z"
      fill="white"
    />,
    <path
      key="86"
      d="M31.5741 219.774H31.9371V218.7C31.9371 218.574 31.9713 218.478 32.0396 218.412C32.1068 218.344 32.1925 218.311 32.2967 218.311C32.4009 218.311 32.4861 218.344 32.5521 218.412C32.6193 218.478 32.6528 218.574 32.6528 218.7V219.774H33.0159V218.575C33.0101 218.369 32.9458 218.212 32.8231 218.106C32.6992 218 32.5625 217.948 32.4131 217.948C32.2197 217.948 32.0634 218.019 31.9441 218.161H31.9371V217.97H31.5741V219.774Z"
      fill="white"
    />
  );

  if (props.lidOpen) {
    tagList.push(
      <rect key="87" x="95" y="133" width="77" height="4" fill="#999999" />,
      <path key="88" d="M95 137H172L176.5 152H95V137Z" fill="#777777" />,
      <path
        key="89"
        d="M172 137V133L180.5 152H176.5L172 137Z"
        fill="#555555"
      />,
      <path key="90" d="M145 141H127L128 149H149L145 141Z" fill="#333333" />,
      <rect key="91" x="95" y="62" width="77" height="71" fill="#0063C3" />,
      <rect key="92" x="95" y="129" width="77" height="4" fill="#004F9C" />,
      <rect key="93" x="95" y="64" width="77" height="2" fill="#004F9C" />,
      <rect key="94" x="99" y="132" width="2" height="5" fill="#0063C3" />,
      <rect key="95" x="107" y="132" width="2" height="5" fill="#0063C3" />,
      <rect key="96" x="103" y="132" width="2" height="5" fill="#0063C3" />,
      <rect key="97" x="158" y="132" width="2" height="5" fill="#0063C3" />,
      <rect key="98" x="166" y="132" width="2" height="5" fill="#0063C3" />,
      <rect key="99" x="162" y="132" width="2" height="5" fill="#0063C3" />,
      <path
        key="100"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M174 62H94V64H118.376C119.93 65.7171 126.111 67 133.5 67C140.889 67 147.07 65.7171 148.624 64H174V62Z"
        fill="#66A1DB"
      />
    );
    if (props.cuvetteId > 0 || props.cuvetteId === 0) {
      tagList.push(
        <rect
          x="127.5"
          y="132.5"
          width="19"
          height="10"
          fill="#E5ECF0"
          fill-opacity="0.5"
          stroke="#668BA6"
        />,
        <mask id="path-46-inside-1_6210_427186" fill="white">
          <path d="M127 132L128 140V150L127 142V132Z" />
        </mask>,
        <path
          d="M127 132L128 140V150L127 142V132Z"
          fill="#E5ECF0"
          fill-opacity="0.5"
        />,
        <path
          d="M127 132L127.992 131.876L126 132H127ZM128 140H129V139.938L128.992 139.876L128 140ZM128 150L127.008 150.124L129 150H128ZM127 142H126V142.062L126.008 142.124L127 142ZM126.008 132.124L127.008 140.124L128.992 139.876L127.992 131.876L126.008 132.124ZM127 140V150H129V140H127ZM128.992 149.876L127.992 141.876L126.008 142.124L127.008 150.124L128.992 149.876ZM128 142V132H126V142H128Z"
          fill="#668BA6"
          mask="url(#path-46-inside-1_6210_427186)"
        />,
        <path
          d="M147.5 143.425V136.062L148.5 140.062V146.675L147.5 143.425Z"
          fill="#E5ECF0"
          fill-opacity="0.5"
          stroke="#668BA6"
        />,
        <path d="M128 143H146L148.5 149H128V143Z" fill="#00AEEF" />,
        <rect
          x="128.5"
          y="140.5"
          width="20"
          height="9"
          fill={getSolutionColor(props.solution)}
          stroke="#668BA6"
        />,
        <path d="M127 132H147L149 138H127V132Z" fill="#ED3431" />,
        <path d="M127 138H149V140H127V138Z" fill="#8E1F1D" />,
        <rect x="129" y="141" width="19" height="3" fill="#5F1514" />
      );
    }
  } else {
    tagList.push(
      <path
        key="87"
        d="M95 132C95 130.895 95.8954 130 97 130H170.139C170.96 130 171.698 130.502 172 131.266L179 149H95V132Z"
        fill="#3382CF"
      />,
      <path
        key="88"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M179 149H95V153C95 154.105 95.8954 155 97 155H121.71C123.685 159.057 129.56 162 136.5 162C143.44 162 149.315 159.057 151.29 155H177C178.105 155 179 154.105 179 153V149Z"
        fill="#66A1DB"
      />
    );
  }

  return (
    <svg
      className={props.className}
      width="200"
      height="240"
      viewBox="0 0 200 240"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {tagList}
    </svg>
  );
}

export function HowItWorkImage() {
  return (
    <Image
      src={InsideSpectrophotometer}
      alt="A flowchart with 7 steps titled “Inside a spectrophotometer.” It shows how light travels from the light source, through the parts of the spectrophotometer and then to the final absorbance reading. 1. Light source. 2. Lens. 3. Prism. 4. The separated spectrum of light hits the wavelength selector, which in turn only lets the yellow light thought to the cuvette. 5. An arrow of light entering and exiting a cuvette. The exiting arrow is dimmer than the entering arrow. 6. Light detector. 7. Spectrophotometer display showing absorbance value."
    />
  );
}

export function TheMathImage() {
  return (
    <Image
      src={TheMathSpectrophotometer}
      alt="A bright yellow arrow labeled “Entering light (I0)” points to a cuvette. A dimmer yellow arrow labeled “Exiting light (I)” points away from the cuvette."
    />
  );
}

export function ThePrincipleOfSpectrophotometryImage() {
  return (
    <Image
      src={PrincipleOfSpectrophotometry}
      alt="Two panels, A and B, compare spectrophotometry using samples with high and low concentrations. Both panels show light entering and exiting a test tube. The test tubes contain a blue solution and a substance depicted as red dots. In panel A (“High concentration”), there are many red dots in the tube. A thick arrow labeled “Entering light” points at the tube. A very thin arrow labeled “Exiting light” points away from the tube. In panel B (“Low concentration”), the tube contains fewer red dots and there isn't much difference in the thickness of the 2 arrows."
    />
  );
}

export function MeasuringSampleSpectrophotometryImage() {
  return (
    <Image
      src={MeasuringSampleSpectrophotometry}
      alt="A bright yellow arrow representing light passes through a cuvette. The image is labeled “times 2."
    />
  );
}

export function WhatIsLightSpectrophotometryImage() {
  return (
    <Image
      src={WhatIsLightSpectrophotometry}
      alt="Visible light is a continuous spectrum of colors ranging from shades of purple and blue to green, yellow, orange, and red between 380 and 750 nanometers. Visible light lies between UV light (between 1 and 380 nanometers) and infrared light (between 750 and 1000 nanometers)."
    />
  );
}

export function SpectrophotometerSubstanceSSpectrumImage() {
  return (
    <Image
      src={SpectrophotometerSustanceSSpectrum}
      alt="A line graph. The x-axis (labeled Wavelength) runs from 350 to 700 nanometers. The y-axis (labeled Absorbance) runs from below 0.0 to 1.0. A line on the graph shows the absorbance for “Substance S.” The line begins at an absorbance of almost 0.3 at 350 nanometers, rises quickly to an absorbance of just over 0.8 by 400 meters, and then decreases to an absorbance of about 0.1 by 500 nanometers. It remains at an absorbance of 0.05 through to 700 nanometers."
    />
  );
}

export const Info1 = () =>
  React.createElement(Image, {
    src: SpectrophotometerInfo1,
    style: { width: "auto", height: "100%" },
    alt: "A spectrophotometer is a rectangular device, similar to a desktop printer. It has a display screen, control buttons, and a chamber with a lid.",
  });

export const Info2 = () =>
  React.createElement(Image, {
    src: SpectrophotometerInfo2,
    style: { width: "auto", height: "100%" },
    alt: "A spectrophotometer. The power button is labeled.",
  });

export const Info3 = () =>
  React.createElement(Image, {
    src: SpectrophotometerInfo3,
    style: { width: "auto", height: "100%" },
    alt: "A spectrophotometer. The zero button is labeled.",
  });

export const Info4 = () =>
  React.createElement(Image, {
    src: SpectrophotometerInfo4,
    style: { width: "auto", height: "100%" },
    alt: "A spectrophotometer. The wavelength dial is labeled. ",
  });

export const Info5 = () =>
  React.createElement(Image, {
    src: SpectrophotometerInfo5,
    style: { width: "auto", height: "100%" },
    alt: "A spectrophotometer. The absorbance display is labeled. ",
  });

export const Info6 = () =>
  React.createElement(Image, {
    src: SpectrophotometerInfo6,
    style: { width: "auto", height: "100%" },
    alt: "A spectrophotometer. The chamber lid is labeled. ",
  });

export const Info7 = () =>
  React.createElement(Image, {
    src: SpectrophotometerInfo7,
    style: { width: "auto", height: "100%" },
    alt: "A spectrophotometer. The open chamber is labeled. ",
  });

export const InsideSpectrophotometerImage = () =>
  React.createElement(Image, {
    src: InsideSpectrophotometer,
    style: { width: "auto", height: "100%" },
    alt: "A spectrophotometer. The open chamber is labeled. ",
  });

function getButtonImage(
  buttonConfig: { hover: string; clicked: string; default: string },
  buttonStatus: BTN_STATUS
) {
  switch (buttonStatus) {
    case BTN_STATUS.HOVER:
      return buttonConfig.hover;
    case BTN_STATUS.CLICK:
    case BTN_STATUS.DISABLED:
      return buttonConfig.clicked;
    default:
      return buttonConfig.default;
  }
}
